/**
 * What a note says, as structure rather than markup: the inbox draws it and Telegram
 * renders it as HTML. Nobody's words can smuggle formatting in, because no string
 * is ever parsed for it.
 */
import { z } from 'zod';

const toneSchema = z.enum(['up', 'down']);

const spanSchema = z.union([
  z.string(),
  z.object({ b: z.string() }),
  z.object({ n: z.string(), tone: toneSchema.optional() }),
  z.object({ c: z.string() }),
]);

export const lineSchema = z.array(spanSchema);

const tableSchema = z.object({
  caption: z.string().optional(),
  head: z.array(z.string()).optional(),
  rows: z.array(z.object({ label: z.string(), cells: z.array(spanSchema) })),
  foot: z.string().optional(),
});

const blockSchema = z.union([
  z.object({ p: lineSchema }),
  z.object({ facts: z.array(z.tuple([z.string(), spanSchema])) }),
  z.object({ table: tableSchema }),
  z.object({ list: z.array(lineSchema) }),
  /** Empty words leave just the link, named by `label`. */
  z.object({ quote: z.string(), href: z.string().optional(), label: z.string().optional() }),
  z.object({ warn: lineSchema }),
]);

export const copySchema = z.object({
  lead: lineSchema,
  more: z.array(blockSchema),
});

export type Span = z.infer<typeof spanSchema>;
export type Line = z.infer<typeof lineSchema>;
export type Block = z.infer<typeof blockSchema>;
export type Table = z.infer<typeof tableSchema>;
export type Copy = z.infer<typeof copySchema>;
export type Fact = [label: string, value: Span];

export const b = (text: string): Span => ({ b: text });

export const n = (text: string, tone?: 'up' | 'down'): Span =>
  tone === undefined ? { n: text } : { n: text, tone };

export const c = (text: string): Span => ({ c: text });

export function line(strings: TemplateStringsArray, ...spans: (Span | number)[]): Line {
  const out: Span[] = [];
  strings.forEach((text, i) => {
    if (text !== '') out.push(text);
    const span = spans[i];
    if (span !== undefined) out.push(typeof span === 'number' ? String(span) : span);
  });
  return out;
}

export const joined = (lines: readonly Line[], by = ', and '): Line =>
  lines.flatMap((said, i) => (i === 0 ? said : [by, ...said]));

export const p = (said: Line | string): Block => ({ p: typeof said === 'string' ? [said] : said });

export const warn = (said: Line | string): Block => ({
  warn: typeof said === 'string' ? [said] : said,
});

export const facts = (...rows: (Fact | false | null)[]): Block => ({
  facts: rows.filter((row): row is Fact => Array.isArray(row)),
});

export const table = (drawn: Table): Block => ({ table: drawn });

export function copy(lead: Line | string, ...more: (Block | false | null | undefined)[]): Copy {
  return {
    lead: typeof lead === 'string' ? [lead] : lead,
    more: more.filter(
      (block): block is Block =>
        typeof block === 'object' &&
        block !== null &&
        !('facts' in block && block.facts.length === 0),
    ),
  };
}

const textOf = (span: Span): string =>
  typeof span === 'string' ? span : 'b' in span ? span.b : 'n' in span ? span.n : span.c;

export const plainLine = (said: Line): string => said.map(textOf).join('');

export function tableText(drawn: Table): string[] {
  const grid = [
    ...(drawn.head ? [['', ...drawn.head]] : []),
    ...drawn.rows.map((one) => [one.label, ...one.cells.map(textOf)]),
  ];
  const widths = Array.from({ length: Math.max(...grid.map((cells) => cells.length)) }, (_, i) =>
    Math.max(...grid.map((cells) => (cells[i] ?? '').length)),
  );
  return grid.map((cells) =>
    cells
      .map((cell, i) => (i === 0 ? cell.padEnd(widths[i] ?? 0) : cell.padStart(widths[i] ?? 0)))
      .join('  ')
      .trimEnd(),
  );
}

function plainBlock(block: Block): string {
  if ('p' in block) return plainLine(block.p);
  if ('table' in block)
    return [block.table.caption, ...tableText(block.table), block.table.foot]
      .filter((part): part is string => part !== undefined)
      .join('\n');
  if ('facts' in block)
    return block.facts.map(([label, value]) => `${label} ${textOf(value)}`).join(' · ');
  if ('list' in block) return block.list.map((item) => `• ${plainLine(item)}`).join('\n');
  if ('quote' in block) {
    const link = block.href ? `${block.label ? `${block.label}: ` : ''}${block.href}` : '';
    return block.quote === '' ? link : `“${block.quote}”${link ? `\n${link}` : ''}`;
  }
  return `⚠️ ${plainLine(block.warn)}`;
}

export const plainCopy = (said: Copy): string =>
  [plainLine(said.lead), ...said.more.map(plainBlock)].join('\n\n');
